import React from 'react';
import type {AlertProps as MaterialAlertProps} from '@mui/material/Alert';
import MaterialAlert from '@mui/material/Alert';
import {alpha, styled} from '@mui/material/styles';
import Info from '@mui/icons-material/Info';
import Close from '@mui/icons-material/Close';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Warning from '@mui/icons-material/Warning';
import Report from '@mui/icons-material/Report';

import {getAlertColor, FuelAlertVariant} from '../helpers/getColor';

import {IconButton} from '../Button/IconButton';
import {Typography} from '../Typography/Typography';

export type {FuelAlertVariant};

export interface AlertProps extends Omit<MaterialAlertProps, 'severity' | 'color' | 'action' | 'iconMapping' | 'square' | 'elevation' | 'closeText'> {
    /**
     * The main text, or the title for the alert.
     */
    title?: string;
    /**
     * Alert variant.
     * @default info
     */
    variant?: FuelAlertVariant;
    /**
     * The component maps the variant prop to a range of different icons, for
     * instance success to `<SuccessOutlined>`. If you wish to change this mapping,
     * you can provide your own. Alternatively, you can use the icon prop to override
     * the icon displayed.
     */
    iconMapping?: Record<FuelAlertVariant, React.ReactNode>;
    /**
     * Prop for ToastProvider use in enqueueToast() function. Makes the toast closeable, without persistence
     */
    isCloseable?: boolean;
    /**
     * Prop for ToastProvider use in enqueueToast() function. Makes the toast closeable with persistence
     */
    isPersistent?: boolean;
    /**
     * Close button text used by assistive technologies to improve accessibility.
     */
    closeText?: string;
}

export const defaultAlertIconMap = {
    default: (
        <Info
            aria-label='Info:'
            titleAccess='Info:'
        />
    ),
    info: (
        <Info
            aria-label='Info:'
            titleAccess='Info:'
        />
    ),
    success: (
        <CheckCircle
            aria-label='Success:'
            titleAccess='Success:'
        />
    ),
    warning: (
        <Warning
            aria-label='Warning:'
            titleAccess='Warning:'
        />
    ),
    error: (
        <Report
            aria-label='Error:'
            titleAccess='Error:'
        />)
};

const getContent = (title?: string, children?: React.ReactNode): React.ReactNode => {
    if (title && children) {
        return (
            <>
                <Typography
                    className='MuiAlert-title'
                    fontWeight='semibold'
                >{title}</Typography>
                <Typography
                    className='MuiAlert-description'
                    variant='body2'
                >{children}</Typography>
            </>
        );
    }

    if (title && !children) {
        return (
            <Typography className='MuiAlert-title'>{title}</Typography>
        );
    }

    return (
        <Typography className='MuiAlert-title'>{children}</Typography>
    );
};

const StyledAlert = styled(MaterialAlert)<AlertProps>(({
    theme, variant
}) => ({
    borderWidth: '0.0625rem',
    borderStyle: 'solid',
    borderRadius: '0.25rem',
    color: theme.tokens.FuelColorTextPrimary,
    fontSize: '1rem',
    lineHeight: '1.5rem',
    padding: '0.75rem 1rem',

    borderColor: getAlertColor(theme.tokens, 'info', 'border'),
    backgroundColor: getAlertColor(theme.tokens, 'info', 'background'),

    ...variant && {
        borderColor: getAlertColor(theme.tokens, variant, 'border'),
        backgroundColor: getAlertColor(theme.tokens, variant, 'background')
    },
    '& .MuiAlert-title': {
        lineHeight: '1.5rem'
    },
    '& .MuiAlert-title + .MuiAlert-description': {
        marginTop: '0.125rem'
    },
    '& .MuiSvgIcon-root': {
        lineHeight: '1.5rem',
        fontSize: 'inherit',

        color: getAlertColor(theme.tokens, 'info', 'icon'),
        ...variant && {
            color: getAlertColor(theme.tokens, variant, 'icon')
        }
    },
    '& .MuiAlert-closeIcon': {
        lineHeight: '1.5rem',
        height: '1.5rem',
        width: '1.5rem',
        padding: '0rem',
        display: 'flex',
        '& .MuiAlert-closeIconButton': {
            backgroundColor: alpha(theme.tokens.FuelColorGray200, 0.0),
            lineHeight: '1.5rem',
            height: '1.5rem',
            width: '1.5rem',
            padding: '0rem',
            '& .MuiSvgIcon-root': {
                height: '1.5rem',
                width: '1.5rem',
                fill: theme.tokens.FuelColorGray800
            },
            '&:hover': {
                backgroundColor: alpha(theme.tokens.FuelColorGray200, 0.75),
                '& .MuiSvgIcon-root': {
                    fill: theme.tokens.FuelColorBlack
                }
            }
        }
    },
    '& .MuiAlert-icon': {
        opacity: 1, // Material Override
        padding: '0rem',
        marginRight: '0.75rem',
        '& .MuiSvgIcon-root': {
            height: '1.5rem',
            width: '1.5rem'
        }
    },
    '& .MuiAlert-message': {
        padding: '0rem'
    },
    '& .MuiAlert-action': {
        alignItems: 'start',
        lineHeight: '1.5rem',
        paddingLeft: '0.75rem',
        paddingTop: '0rem',
        paddingBottom: '0rem'
    }
}));

const StyledCloseIcon = styled(Close)(({theme}) => ({
    cursor: 'pointer',
    fill: theme.tokens.FuelColorGray800,
    height: '1.5rem',
    width: '1.5rem',
    transitionDuration: '0ms',
    '&:hover': {
        fill: theme.tokens.FuelColorBlack
    }
}));

export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(({
    title, children, variant = 'info',
    icon, iconMapping, onClose, closeText = 'Close',
    isCloseable, isPersistent, ...props
}, ref) => {
    const alertContent = getContent(title, children);
    const alertIconMap = {
        ...defaultAlertIconMap,
        ...iconMapping
    };

    return (
        <StyledAlert
            action={(onClose && (
                <div className='MuiAlert-closeIcon'>
                    <IconButton
                        aria-label={closeText}
                        className='MuiAlert-closeIconButton'
                        onClick={onClose}
                        size='large'
                    ><StyledCloseIcon/></IconButton>
                </div>
            ))}
            closeText={closeText}
            icon={icon || alertIconMap[variant]}
            onClose={onClose}
            ref={ref}
            variant={variant}
            {...props}
            severity={undefined} // for class generation
        >
            {alertContent}
        </StyledAlert>
    );
});

Alert.displayName = 'Alert';
